// src/App.js
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from "react-router-dom";
import './App.css';
import { getHomeForUser, getUser } from './lib/assets.js';
import DoneScreen from './pages/DoneScreen.js';
import LoginScreen from './pages/LoginScreen';
import ParticipantScreen from './pages/ParticipantScreen';
import WelcomeScreen from './pages/WelcomeScreen';

function generateSensorDataForDate(targetDate) {
  const data = [];

  const lightValues = [100, 150, 230, 450, 509]; // slots 0–4

  for (let i = 0; i < 96; i++) {
    const slotTime = new Date(targetDate);
    slotTime.setHours(0, 0, 0, 0);
    slotTime.setMinutes(i * 15);

    // Temperature & Humidity
    let temperature = i === 0 ? 22.0 : 27.2;
    let humidity = i === 0 ? 45.0 : 48.5;

    // Light
    const light = i < lightValues.length ? lightValues[i] : 0;

    // Motion - Custom per room and slot
    let kitchenMotion = 0;
    let livingRoomMotion = 0;
    let bathroomMotion = 0;
    let bedroomMotion = 0;

    if (i === 0) {
      kitchenMotion = 10;
    } else if (i === 1) {
      kitchenMotion = 15;     // +5 from slot 0
    } else if (i === 2) {
      kitchenMotion = 8;      // decrease
      livingRoomMotion = 12;  // increase
    } else if (i === 3) {
      kitchenMotion = 5;      // decrease
      livingRoomMotion = 18;  // increase
      bathroomMotion = 15;    // sudden rise
    }

    const kitchen = {
      temperature: Number(temperature.toFixed(1)),
      light,
      motion: kitchenMotion,
      humidity: Number(humidity.toFixed(1)),
    };
    const livingRoom = {
      temperature: Number(temperature.toFixed(1)),
      light,
      motion: livingRoomMotion,
      humidity: Number(humidity.toFixed(1)),
    };
    const bedroom = {
      temperature: Number(temperature.toFixed(1)),
      light,
      motion: bedroomMotion,
      humidity: Number(humidity.toFixed(1)),
    };
    const bathroom = {
      temperature: Number(temperature.toFixed(1)),
      light,
      motion: bathroomMotion,
      humidity: Number(humidity.toFixed(1)),
    };

    data.push({
      timestamp: slotTime.toISOString(),
      start_at: slotTime.getTime() / 1000,
      kitchen,
      livingRoom,
      bedroom,
      bathroom,
      adl: null,
    });
  }
  return data;
}

async function gatherSensors() {
  const sensors = { kitchen: {}, livingRoom: {}, bedroom: {}, bathroom: {}, unknown: {} };
  try {
    const user = await getUser();
    const home = await getHomeForUser(user);
    if (!home) return sensors;
    home.locations.forEach(room => {
      let roomName = "unknown";
      if (room.label?.match(/living.*/i)) roomName = 'livingRoom';
      else if (room.label?.match(/kitchen.*/i)) roomName = "kitchen";
      else if (room.label?.match(/bed.*/i)) roomName = "bedroom";
      else if (room.label?.match(/bath.*/i)) roomName = "bathroom";
      else roomName = room.label?room.label:'unknown'
      room.devices.forEach(device => {
        device.sensors.forEach(sensor => {
          if (sensor.tags.find(t => t.name === "Temperature") || sensor.name.match(/temperature/i))
            sensors[roomName]["temperature"] = sensor.id;
          else if (sensor.tags.find(t => t.name === "Light") || sensor.name.match(/luminance/i))
            sensors[roomName]["light"] = sensor.id;
          else if (sensor.tags.find(t => t.name === "Presence") || sensor.name.match(/motion/i))
            sensors[roomName]["motion"] = sensor.id;
          else if (sensor.tags.find(t => t.name === "Humidity") || sensor.name.match(/humidity/i))
            sensors[roomName]["humidity"] = sensor.id;
        });
      });
    });
    return sensors;
  } catch (err) {
    console.error(err);
    return sensors;
  }
}

function App() {
  const [targetDate, setTargetDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [data, setData] = useState(() => generateSensorDataForDate(targetDate));
  const [sensors, setSensors] = useState(null);
  const [hasCaregiver, setHasCaregiver] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    gatherSensors().then(setSensors).catch(err => {
      setError('Failed to load sensor data');
      console.error(err);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const newYesterday = new Date();
      newYesterday.setDate(new Date().getDate() - 1);
      newYesterday.setHours(0, 0, 0, 0);
      if (newYesterday.getTime() !== targetDate.getTime()) {
        setTargetDate(newYesterday);
        setData(generateSensorDataForDate(newYesterday));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [targetDate]);

  function resetData() {
    setData(generateSensorDataForDate(targetDate));
  }

  if (error) return <div className="App">Error: {error}</div>;
  if (!sensors) return <div className="App">Loading sensors...</div>;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route index element={<WelcomeScreen setHasCaregiver={setHasCaregiver} />} />
          <Route path="participant" element={
            <ParticipantScreen
              data={data}
              sensors={sensors}
              setData={setData}
              isCaregiverScreen={false}
              hasCaregiver={hasCaregiver}
              targetDate={targetDate}
            />
          } />
          <Route path="caregiver" element={
            <ParticipantScreen
              data={data}
              sensors={sensors}
              setData={setData}
              isCaregiverScreen={true}
              hasCaregiver={hasCaregiver}
              targetDate={targetDate}
            />
          } />
          <Route path="login" element={<LoginScreen />} />
          <Route path="done" element={<DoneScreen resetData={resetData} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;